import { createClient } from './supabase';
import type { Event, EventRegistration } from '@/types/events';
import type { UserProfile } from '@/types/profile';
import { handleError } from './error-handler';

export class EventsService {
  // Get all events with optional filtering by charity or business ID
  async getAll(filters?: { charityId?: string; businessId?: string }): Promise<Event[]> {
    try {
      const supabase = await createClient();
      let query = supabase.from('events').select('*');
      
      if (filters?.charityId) {
        query = query.eq('created_by', filters.charityId);
      } else if (filters?.businessId) {
        query = query.eq('created_by', filters.businessId);
      } else {
        // If no filters provided, get events for the current user's business/charity
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('business_id')
          .single();
        
        if (!profileError && profileData?.business_id) {
          query = query.eq('created_by', profileData.business_id);
        }
      }
      
      const { data, error } = await query;
      
      if (error) throw error;
      return data as Event[];
    } catch (error) {
      return handleError(error);
    }
  }

  // Get a specific event by ID
  async get(id: string): Promise<Event> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('id', id)
        .single();
      
      if (error) throw error;
      return data as Event;
    } catch (error) {
      return handleError(error);
    }
  }

  // Create a new event
  async create(event: Partial<Event>): Promise<Event> {
    try {
      const supabase = await createClient();
      
      // First get the user's profile to find their business_id
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('business_id')
        .single();
      
      if (profileError) throw profileError;
      
      // Create the event
      const eventData = {
        ...event,
        created_by: profileData.business_id,
        created_at: new Date().toISOString()
      };
      
      const { data, error } = await supabase
        .from('events')
        .insert(eventData)
        .select()
        .single();
      
      if (error) throw error;
      return data as Event;
    } catch (error) {
      return handleError(error);
    }
  }

  // Update an existing event
  async update(id: string, event: Partial<Event>): Promise<Event> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('events')
        .update({
          ...event,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data as Event;
    } catch (error) {
      return handleError(error);
    }
  }

  // Delete an event
  async delete(id: string): Promise<void> {
    try {
      const supabase = await createClient();
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    } catch (error) {
      return handleError(error);
    }
  }

  // Register a user for an event
  async registerForEvent(eventId: string, userId: string): Promise<EventRegistration> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('attend_event')
        .insert({
          event_id: eventId,
          user_id: userId,
          created_at: new Date().toISOString()
        })
        .select()
        .single();
      
      if (error) throw error;
      return data as EventRegistration;
    } catch (error) {
      return handleError(error);
    }
  }

  // Unregister a user from an event
  async unregister(eventId: string, userId: string): Promise<void> {
    try {
      const supabase = await createClient();
      const { error } = await supabase
        .from('attend_event')
        .delete()
        .eq('event_id', eventId)
        .eq('user_id', userId);
      
      if (error) throw error;
      return;
    } catch (error) {
      return handleError(error);
    }
  }

  // Get all registrations for an event
  async getRegistrations(eventId: string): Promise<(EventRegistration & { profiles: UserProfile })[]> {
    try {
      const supabase = await createClient();
      const { data, error } = await supabase
        .from('attend_event')
        .select('*, profiles(*)')
        .eq('event_id', eventId);
      
      if (error) throw error;
      return data as (EventRegistration & { profiles: UserProfile })[];
    } catch (error) {
      return handleError(error);
    }
  }
}

// Create and export a singleton instance
export const eventsService = new EventsService();
